/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Share actions for kennisbank articles: copy-link + LinkedIn/WhatsApp/X
 * intent links (plain anchors — no SDK scripts, CSP stays untouched) plus the
 * first-party share counter ("N× gedeeld").
 *
 * The state lives in ONE `useArticleShare` hook instance (owned by
 * ArticlePage) shared by both render surfaces — the docked row in the bottom
 * bar here, and the floating scroll pill in ArticleFloatingActions.tsx — so
 * the copied-feedback, optimistic count and localStorage suppression can't
 * diverge or double-count between them.
 *
 * Tracking: every click fires a per-method analytics event
 * (article_share_copy / _linkedin / _whatsapp / _x) through the full pipeline
 * (GA4 + /api/event-ping) — per-method names on purpose, because the
 * first-party sink keeps name-only counters, so each channel gets its own row
 * in the admin Events panel. `variant` says which rendering was clicked
 * ('dock' = bottom-bar row, 'pill' = floating pill) per the taxonomy rule for
 * multi-rendering surfaces. The visible counter is a separate first-party
 * ping (/api/article-share-ping), sent once per (article, method) per browser
 * via localStorage suppression — see src/lib/articleShareMemory.ts.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Check, Link2, Linkedin, Twitter } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { trackEvent, SURFACES } from '../lib/gtag';
import { formatInteger } from '../lib/formatters';
import { markShareCounted, type ShareMethod } from '../lib/articleShareMemory';

export interface ArticleShareArgs {
  articleId: string;
  slug: string;
  /** Canonical absolute URL — passed in so SSR and client render identically. */
  url: string;
  title: string;
  /** Lifetime share count from the article payload (SSR-seeded). */
  initialShareCount?: number;
}

// Lucide ships no brand glyphs, so WhatsApp gets the official logo as an
// inline SVG (simple-icons path data, CC0) — a generic chat bubble wasn't
// recognizable as WhatsApp. Same currentColor/size contract as lucide icons.
export const WhatsAppIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

export interface ShareIntent {
  method: Exclude<ShareMethod, 'copy'>;
  href: string;
  labelKey: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

/** Which rendering of the share controls was clicked. */
export type ShareVariant = 'dock' | 'pill';

export interface ArticleShareState {
  copied: boolean;
  shareCount: number;
  onCopy: (variant: ShareVariant) => void;
  intents: ShareIntent[];
  recordShare: (method: ShareMethod, variant: ShareVariant) => void;
}

function sendShareCountPing(articleId: string, method: ShareMethod): void {
  const body = JSON.stringify({ id: articleId, m: method });
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      if (navigator.sendBeacon('/api/article-share-ping', body)) return;
    }
  } catch {
    /* fall through to fetch */
  }
  try {
    void fetch('/api/article-share-ping', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* best effort — never break the share action over analytics */
  }
}

/**
 * One hook instance per article page; `args` may be null while the article is
 * still loading (hooks must be unconditional). Handlers no-op until it's set.
 */
export function useArticleShare(args: ArticleShareArgs | null): ArticleShareState {
  const { language } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [shareCount, setShareCount] = useState(args?.initialShareCount ?? 0);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    []
  );
  // A fresher count can arrive after mount (stale-while-revalidate refetch),
  // and SPA navigation to another article resets the floor.
  const initial = args?.initialShareCount;
  const articleId = args?.articleId;
  useEffect(() => {
    if (typeof initial === 'number') {
      setShareCount((prev) => Math.max(prev, initial));
    }
  }, [initial]);
  useEffect(() => {
    // New article on the same mounted page: drop the previous article's count.
    setShareCount(initial ?? 0);
    setCopied(false);
  }, [articleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const recordShare = (method: ShareMethod, variant: ShareVariant) => {
    if (!args) return;
    trackEvent(`article_share_${method}`, {
      surface: SURFACES.article,
      variant,
      language,
      article_slug: args.slug,
    });
    if (markShareCounted(args.articleId, method)) {
      sendShareCountPing(args.articleId, method);
      setShareCount((prev) => prev + 1);
    }
  };

  const onCopy = async (variant: ShareVariant) => {
    if (!args) return;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(args.url);
        setCopied(true);
        if (copiedTimer.current) clearTimeout(copiedTimer.current);
        copiedTimer.current = setTimeout(() => setCopied(false), 2000);
        // Only after a successful write — a failed copy is not a share, and
        // counting it would inflate article_share_copy + the public counter.
        recordShare('copy', variant);
      }
    } catch {
      /* clipboard unavailable — the intent links still work */
    }
  };

  const url = args?.url ?? '';
  const title = args?.title ?? '';
  const intents: ShareIntent[] = [
    {
      method: 'linkedin',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      labelKey: 'kennisbank.share_via_linkedin',
      Icon: Linkedin,
    },
    {
      method: 'whatsapp',
      href: `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
      labelKey: 'kennisbank.share_via_whatsapp',
      Icon: WhatsAppIcon,
    },
    {
      method: 'x',
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      labelKey: 'kennisbank.share_via_x',
      Icon: Twitter,
    },
  ];

  return { copied, shareCount, onCopy, intents, recordShare };
}

const CHIP_CLASS =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800/80 hover:bg-slate-700/80 ' +
  'border border-slate-700 hover:border-emerald-500/40 text-slate-200 text-xs font-bold transition-colors';

/** Docked share row — lives in the article's bottom bar. */
export const ArticleShareRow: React.FC<{ share: ArticleShareState }> = ({ share }) => {
  const { t, language } = useLanguage();
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-slate-400 text-xs font-bold hidden sm:inline">
        {t('kennisbank.share_article')}
      </span>
      <button type="button" onClick={() => share.onCopy('dock')} className={CHIP_CLASS}>
        {share.copied ? (
          <Check aria-hidden="true" className="w-3.5 h-3.5 text-emerald-400" />
        ) : (
          <Link2 aria-hidden="true" className="w-3.5 h-3.5" />
        )}
        <span aria-live="polite">
          {share.copied ? t('kennisbank.share_copied') : t('kennisbank.share_copy_link')}
        </span>
      </button>
      {share.intents.map(({ method, href, labelKey, Icon }) => (
        <a
          key={method}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t(labelKey)}
          title={t(labelKey)}
          onClick={() => share.recordShare(method, 'dock')}
          className={CHIP_CLASS}
        >
          <Icon aria-hidden="true" className="w-3.5 h-3.5" />
        </a>
      ))}
      {share.shareCount >= 1 && (
        <span className="text-slate-400 text-xs font-bold whitespace-nowrap">
          {formatInteger(share.shareCount, language)}&times; {t('kennisbank.share_count_suffix')}
        </span>
      )}
    </div>
  );
};
